// Mock data for "Grow your community" (קידום הקהילה) — FE-first, NOT wired to
// the backend. Two illustrative weeks plus an "all situations" set so review
// can see every share occasion, task type and AI answer. The page's preview
// switcher (or `?week=1|2|all`) picks one.
//
// Real-API shapes this mock stands in for:
//  - share items:   { id, kind, occasion, image, title, detail, vars, link }
//                   `occasion` picks the chip label + message template; `vars`
//                   fill the template; `link` is the item's short, non-indexed
//                   share link (OG title + image, routes by auth/membership).
//  - weekly lists:  refresh together on `resetsAt` (next Sunday here).
//  - weekly tasks:  { id, type, group, vars, route | action }
//                   `group` orders the list (waiting-on-you first).
//  - reply helper:  one POST per request → the answer object built by
//                   `buildAnswer` below (verdict, steps, reply + variants).

export const MAX_WEEKLY_TASKS = 7;

// Waiting-on-you items always lead; setup-style items come last.
export const TASK_GROUP_ORDER = ["waiting", "share", "grow", "life", "setup"];

// Type tags above the share list — each groups item kinds a manager thinks of
// together. Only tags with items this week are shown.
export const SHARE_FILTERS = [
  { key: "events", kinds: ["event"] },
  { key: "suppliers", kinds: ["supplier"] },
  { key: "deals", kinds: ["hatava", "coupon"] },
  { key: "shops", kinds: ["product", "store"] },
  { key: "projects", kinds: ["project", "goal"] },
  { key: "community", kinds: ["community"] },
];

/** Weekly lists refresh at the start of the Israeli week (Sunday). */
export const nextResetDate = (from = new Date()) => {
  const date = new Date(from);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + ((7 - date.getDay()) % 7 || 7));
  return date;
};

const shortLink = (code) => `https://keilot.com/s/${code}`;

// ---------------------------------------------------------------------------
// Share items

const WEEK_1_ITEMS = [
  {
    id: "evt-garden",
    kind: "event",
    occasion: "event_tomorrow",
    image: "/mock-projects/garden.jpg",
    title: { he: "ערב גינון קהילתי", en: "Community gardening evening" },
    detail: { he: "מחר, 19:00 · גינת הבניין", en: "Tomorrow, 7:00 PM · Building garden" },
    vars: {
      he: { title: "ערב גינון קהילתי", time: "19:00", place: "בגינת הבניין" },
      en: { title: "the community gardening evening", time: "7:00 PM", place: "in the building garden" },
    },
    link: shortLink("g7k2p"),
  },
  {
    id: "proj-club",
    kind: "project",
    occasion: "project_completed",
    image: "/mock-projects/center.jpg",
    title: { he: "שיפוץ מועדון הדיירים", en: "Residents' club renovation" },
    detail: { he: "הפרויקט הושלם", en: "Project completed" },
    vars: { he: { title: "שיפוץ מועדון הדיירים" }, en: { title: "the residents' club renovation" } },
    link: shortLink("c4m9x"),
  },
  {
    id: "goal-month",
    kind: "goal",
    occasion: "goal_almost",
    image: "/mock-projects/fund.jpg",
    title: { he: "היעד החודשי של הקהילה", en: "The community's monthly goal" },
    detail: { he: "הגענו ל-84% מהיעד", en: "84% of the goal reached" },
    vars: { he: { percent: 84 }, en: { percent: 84 } },
    link: shortLink("f2r8d"),
  },
  {
    id: "sup-dana",
    kind: "supplier",
    occasion: "supplier_joined",
    image: "/mysupplier.png",
    title: { he: "דנה שירותי ניקיון", en: "Dana Cleaning Services" },
    detail: { he: "ספק חדש · ניקיון", en: "New supplier · Cleaning" },
    vars: {
      he: { name: "דנה שירותי ניקיון", category: "ניקיון" },
      en: { name: "Dana Cleaning Services", category: "cleaning" },
    },
    link: shortLink("d8n3q"),
  },
  {
    id: "hat-teamim",
    kind: "hatava",
    occasion: "hatava_new",
    image: "/mock-projects/holiday.jpg",
    title: { he: "15% הנחה על מגשי אירוח", en: "15% off party platters" },
    detail: { he: "אצל טעמים קייטרינג", en: "At Teamim Catering" },
    vars: {
      he: { title: "15% הנחה על מגשי אירוח אצל טעמים קייטרינג" },
      en: { title: "15% off party platters at Teamim Catering" },
    },
    link: shortLink("t5h1w"),
  },
  {
    id: "prod-bourekas",
    kind: "product",
    occasion: "product_new",
    image: "/mock-projects/center-a.jpg",
    title: { he: "מגש בורקס ביתי", en: "Homemade bourekas tray" },
    detail: { he: "חדש במאפיית השכונה", en: "New at the Neighborhood Bakery" },
    vars: {
      he: { title: "מגש בורקס ביתי", store: "מאפיית השכונה" },
      en: { title: "a homemade bourekas tray", store: "the Neighborhood Bakery" },
    },
    link: shortLink("b3k7z"),
  },
  {
    id: "sup-avi",
    kind: "supplier",
    occasion: "top_rated",
    image: "/supplier-icon.png",
    title: { he: "אבי אינסטלציה", en: "Avi Plumbing" },
    detail: { he: "הדירוג הגבוה בקהילה החודש · 4.9", en: "Top rated this month · 4.9" },
    vars: { he: { name: "אבי אינסטלציה", rating: "4.9" }, en: { name: "Avi Plumbing", rating: "4.9" } },
    link: shortLink("a9p4l"),
  },
  {
    id: "welcome-w1",
    kind: "community",
    occasion: "welcome_members",
    image: "/mock-projects/garden-b.jpg",
    title: { he: "12 חברים חדשים", en: "12 new members" },
    detail: { he: "הצטרפו השבוע", en: "Joined this week" },
    vars: { he: { count: 12 }, en: { count: 12 } },
    link: shortLink("w1j6e"),
    // Demo: shows how a row looks once it was shared.
    initiallyShared: true,
  },
];

const WEEK_2_ITEMS = [
  {
    id: "evt-baking",
    kind: "event",
    occasion: "event_few_spots",
    image: "/mock-projects/holiday.jpg",
    title: { he: "סדנת אפייה לילדים", en: "Kids' baking workshop" },
    detail: { he: "נשארו 4 מקומות", en: "4 spots left" },
    vars: { he: { title: "סדנת אפייה לילדים", count: 4 }, en: { title: "the kids' baking workshop", count: 4 } },
    link: shortLink("k2b8s"),
  },
  {
    id: "evt-thanks",
    kind: "event",
    occasion: "event_thanks",
    image: "/mock-projects/garden.jpg",
    title: { he: "ערב גינון קהילתי", en: "Community gardening evening" },
    detail: { he: "תודה שהגעתם", en: "Thanks for coming" },
    vars: { he: { title: "ערב הגינון הקהילתי" }, en: { title: "the community gardening evening" } },
    link: shortLink("g7k2p"),
  },
  {
    id: "cat-piano",
    kind: "supplier",
    occasion: "category_new",
    image: "/supplier-icon.png",
    title: { he: "נועה מוזיקה", en: "Noa Music" },
    detail: { he: "מורה לפסנתר · חדש בקהילה", en: "Piano teacher · New in the community" },
    vars: {
      he: { category: "מורה לפסנתר", name: "נועה מוזיקה" },
      en: { category: "a piano teacher", name: "Noa Music" },
    },
    link: shortLink("n6m2v"),
  },
  {
    id: "hat-coffee",
    kind: "hatava",
    occasion: "hatava_ending",
    image: "/mock-projects/fund-a.jpg",
    title: { he: "1+1 על קפה", en: "Buy one, get one coffee" },
    detail: { he: "בקפה פינה · מסתיימת בקרוב", en: "At Cafe Pina · Ending soon" },
    vars: { he: { title: "1+1 על קפה בקפה פינה" }, en: { title: "buy one, get one coffee at Cafe Pina" } },
    link: shortLink("p1c5y"),
  },
  {
    id: "store-ruti",
    kind: "store",
    occasion: "store_opened",
    image: "/mock-projects/center-a.jpg",
    title: { he: "המטבח של רותי", en: "Ruti's Kitchen" },
    detail: { he: "נפתח להזמנות בקהילה", en: "Now taking community orders" },
    vars: { he: { name: "המטבח של רותי" }, en: { name: "Ruti's Kitchen" } },
    link: shortLink("r4k9t"),
  },
  {
    id: "proj-roof",
    kind: "project",
    occasion: "project_new",
    image: "/mock-projects/garden-a.jpg",
    title: { he: "גינה קהילתית על הגג", en: "Rooftop community garden" },
    detail: { he: "פרויקט חדש בקהילה", en: "New community project" },
    vars: { he: { title: "גינה קהילתית על הגג" }, en: { title: "a rooftop community garden" } },
    link: shortLink("h3g8u"),
  },
  {
    id: "milestone-150",
    kind: "community",
    occasion: "members_milestone",
    image: "/people.png",
    title: { he: "150 חברים בקהילה", en: "150 community members" },
    detail: { he: "אבן דרך לקהילה", en: "A community milestone" },
    vars: { he: { count: 150 }, en: { count: 150 } },
    link: shortLink("m150a"),
  },
  {
    id: "second-hand",
    kind: "community",
    occasion: "second_hand_count",
    image: "/shirt.png",
    title: { he: "18 פריטים ביד שנייה", en: "18 second-hand items" },
    detail: { he: "פורסמו השבוע", en: "Posted this week" },
    vars: { he: { count: 18 }, en: { count: 18 } },
    link: shortLink("s18ya"),
  },
];

// Occasions that only appear in "all situations".
const EXTRA_ITEMS = [
  {
    id: "goal-reached",
    kind: "goal",
    occasion: "goal_reached",
    image: "/mock-projects/fund-a.jpg",
    title: { he: "הגענו ליעד החודשי!", en: "We reached the monthly goal!" },
    detail: { he: "היעד של הקהילה הושלם", en: "The community goal is complete" },
    vars: {},
    link: shortLink("f9r2d"),
  },
  {
    id: "sup-spark",
    kind: "supplier",
    occasion: "top_booked",
    image: "/mysupplier.png",
    title: { he: "ספארק ניקיון", en: "Spark Cleaning" },
    detail: { he: "הכי מוזמנים בקהילה החודש", en: "Most booked in the community this month" },
    vars: { he: { name: "ספארק ניקיון" }, en: { name: "Spark Cleaning" } },
    link: shortLink("s2k8c"),
  },
  {
    id: "coupon-gal",
    kind: "coupon",
    occasion: "coupon_popular",
    image: "/sales-discount.png",
    title: { he: "קופון לתספורת במספרת גל", en: "Haircut coupon at Gal Salon" },
    detail: { he: "הקופון הכי פופולרי השבוע", en: "This week's most popular coupon" },
    vars: { he: { title: "לתספורת במספרת גל" }, en: { title: "for a haircut at Gal Salon" } },
    link: shortLink("q7g3h"),
  },
  {
    id: "milestone-suppliers",
    kind: "community",
    occasion: "suppliers_milestone",
    image: "/supplier-icon.png",
    title: { he: "30 ספקים בקהילה", en: "30 community suppliers" },
    detail: { he: "אבן דרך לקהילה", en: "A community milestone" },
    vars: { he: { count: 30 }, en: { count: 30 } },
    link: shortLink("s30ya"),
  },
];

// ---------------------------------------------------------------------------
// Weekly tasks

const WEEK_1_TASKS = [
  { id: "w1-share-items", type: "share_items", group: "share", vars: { count: 2 }, action: "scrollToShare" },
  { id: "w1-approve-join", type: "approve_join", group: "waiting", vars: { count: 4 }, route: "/my-members" },
  {
    id: "w1-missing-category",
    type: "add_missing_category",
    group: "grow",
    vars: { he: { category: "חשמלאי" }, en: { category: "an electrician" } },
    route: "/find-suppliers?category=Electrical",
  },
  { id: "w1-supplier-offers", type: "supplier_offers", group: "waiting", vars: { count: 2 }, route: "/my-suppliers" },
  {
    id: "w1-event-reminder",
    type: "share_event_reminder",
    group: "share",
    vars: { he: { title: "ערב גינון קהילתי" }, en: { title: "the gardening evening" } },
    action: "scrollToShare",
  },
  { id: "w1-goal-progress", type: "share_goal_progress", group: "share", vars: { percent: 84 }, action: "scrollToShare" },
  {
    id: "w1-suggest-hatava",
    type: "suggest_hatava",
    group: "grow",
    vars: { he: { name: "אבי אינסטלציה" }, en: { name: "Avi Plumbing" } },
    route: "/my-suppliers",
  },
];

const WEEK_2_TASKS = [
  { id: "w2-welcome", type: "welcome_members", group: "grow", vars: { count: 9 } },
  { id: "w2-reports", type: "member_reports", group: "waiting", vars: { count: 3 }, route: "/my-members" },
  { id: "w2-join-link", type: "share_join_link", group: "share", action: "copyJoinLink" },
  {
    id: "w2-follow-up",
    type: "follow_up_invite",
    group: "grow",
    vars: { he: { name: "גל חשמל" }, en: { name: "Gal Electric" } },
    route: "/my-suppliers",
  },
  { id: "w2-create-event", type: "create_event", group: "life" },
  {
    id: "w2-supplier-quality",
    type: "supplier_quality",
    group: "grow",
    vars: { he: { name: "ניר הובלות" }, en: { name: "Nir Movers" } },
    route: "/my-suppliers",
  },
  {
    id: "w2-project-update",
    type: "project_update",
    group: "life",
    vars: { he: { title: "הגינה על הגג" }, en: { title: "the rooftop garden" } },
  },
];

// Task types that only appear in "all situations".
const EXTRA_TASKS = [
  { id: "all-open-answers", type: "finish_saved_answers", group: "life", vars: { count: 2 }, action: "scrollToHelper" },
  { id: "all-group-links", type: "add_group_links", group: "setup", route: "/community-settings", initiallyDone: true },
  { id: "all-monthly-goal", type: "set_monthly_goal", group: "setup", route: "/community-settings" },
];

const ALL_TASKS = [
  ...WEEK_1_TASKS,
  ...WEEK_2_TASKS.map((task) => (task.id === "w2-welcome" ? { ...task, initiallyDone: true } : task)),
  ...EXTRA_TASKS,
];

export const MOCK_WEEKS = {
  1: { items: WEEK_1_ITEMS, tasks: WEEK_1_TASKS },
  2: { items: WEEK_2_ITEMS, tasks: WEEK_2_TASKS },
  // Review only: every occasion, every task type (no cap), every AI answer.
  all: { items: [...WEEK_1_ITEMS, ...WEEK_2_ITEMS, ...EXTRA_ITEMS], tasks: ALL_TASKS, uncapped: true, demoAnswers: true },
};

// The community's member-invite link (normal Onboarding invite).
export const MOCK_JOIN_LINK = shortLink("join-nsh");

// ---------------------------------------------------------------------------
// Reply helper — mocked AI. One call per request; the answer is saved as-is.
// Verdict + steps come back in the CM's UI language; the reply comes back in
// the language of the pasted message. Scenarios are keyword-matched for the
// demo (order matters); the real service reads the community's live data.

const HEBREW = /[֐-׿]/;

const replyOnly = { he: "לענות בהודעה הזו.", en: "Reply with this message." };

const SCENARIOS = [
  {
    key: "electrician",
    match: /חשמל|electric/i,
    status: "action",
    question: { he: "מישהו מכיר חשמלאי טוב?", en: "Does anyone know a good electrician?" },
    verdict: { he: "עדיין אין חשמלאי בקהילה.", en: "There's no electrician in the community yet." },
    steps: [
      {
        text: { he: "למצוא חשמלאי באינדקס הספקים.", en: "Find an electrician in the supplier index." },
        link: "/find-suppliers?category=Electrical",
        linkLabel: { he: "לאינדקס מסונן לחשמלאים", en: "Open the index filtered to electricians" },
      },
      {
        text: {
          he: "להזמין את החשמלאי לקהילה ולחכות להצעה שלו לאישור.",
          en: "Invite the electrician and wait for their offer to approve.",
        },
      },
      { text: { he: "בינתיים, לענות בהודעה הזו:", en: "Meanwhile, reply with this message:" } },
    ],
    replies: {
      he: {
        default: "היי! עדיין אין לנו חשמלאי בקהילה, אבל אנחנו כבר מטפלים בזה. אעדכן כאן ברגע שיצטרף.",
        shorter: "עוד אין לנו חשמלאי בקהילה. אעדכן כאן בקרוב.",
        warmer: "איזו שאלה טובה! עדיין אין לנו חשמלאי בקהילה, אבל אנחנו כבר מחפשים מישהו מעולה בשבילכם. אעדכן כאן ממש בקרוב.",
      },
      en: {
        default: "Hi! We don't have an electrician in the community yet, but we're on it. I'll update here as soon as one joins.",
        shorter: "No electrician in the community yet. I'll update here soon.",
        warmer: "Great question! We don't have an electrician in the community yet, but we're already looking for a great one for you. I'll update here very soon.",
      },
    },
  },
  {
    key: "complaint",
    match: /לא הגיע|לא עונה|לא ענה|תלונה|מאוכזב|didn't show|no-show|never came|complain/i,
    status: "action",
    question: null,
    verdict: {
      he: "זו תלונה על ספק מהקהילה. עדיף לטפל בה בפרטי ולא בקבוצה.",
      en: "This is a complaint about a community supplier. Handle it privately, not in the group.",
    },
    steps: [
      {
        text: { he: "לבדוק את ההזמנה ואת הדירוגים של הספק.", en: "Check the order and the supplier's ratings." },
        link: "/my-suppliers",
        linkLabel: { he: "לספקים של הקהילה", en: "Open community suppliers" },
      },
      { text: { he: "ליצור קשר עם הספק כדי להבין מה קרה.", en: "Contact the supplier to find out what happened." } },
      { text: { he: "לענות בקבוצה בקצרה, ולהמשיך בפרטי:", en: "Reply briefly in the group and continue privately:" } },
    ],
    replies: {
      he: {
        default: "תודה שעדכנת. אנחנו בודקים את זה מול הספק ונחזור אליך בפרטי.",
        shorter: "תודה, בודקים מול הספק ונחזור אליך בפרטי.",
        warmer: "תודה שעדכנת, ממש לא נעים לשמוע. אנחנו כבר בודקים את זה מול הספק ונחזור אליך בפרטי בהקדם.",
      },
      en: {
        default: "Thanks for letting us know. We're checking this with the supplier and will get back to you privately.",
        shorter: "Thanks, checking with the supplier and will message you privately.",
        warmer: "Thanks for letting us know, and sorry this happened. We're already checking with the supplier and will get back to you privately soon.",
      },
    },
  },
  {
    key: "cleaning",
    match: /ניקיון|מנקה|עוזרת בית|clean/i,
    status: "ready",
    question: { he: "יש המלצה על מישהו לניקיון?", en: "Any recommendation for cleaning?" },
    verdict: {
      he: "יש בקהילה 2 ספקי ניקיון, ואחד מהם הצטרף השבוע.",
      en: "The community has 2 cleaning suppliers, and one joined this week.",
    },
    steps: [{ text: { he: "לענות בהודעה הזו. הקישור מוביל ישר לספקים.", en: "Reply with this message. The link goes straight to the suppliers." } }],
    replies: {
      he: {
        default: "יש לנו בקהילה את דנה שירותי ניקיון ואת ספארק ניקיון. אפשר להזמין ישירות דרך האפליקציה של הקהילה: https://keilot.com/s/d8n3q",
        shorter: "דנה שירותי ניקיון וספארק ניקיון, דרך האפליקציה: https://keilot.com/s/d8n3q",
        warmer: "בשמחה! יש לנו בקהילה שני ספקי ניקיון מצוינים, דנה שירותי ניקיון וספארק ניקיון. אפשר להזמין אותם ישירות דרך האפליקציה של הקהילה: https://keilot.com/s/d8n3q",
      },
      en: {
        default: "We have Dana Cleaning Services and Spark Cleaning in the community. You can book them directly in the community app: https://keilot.com/s/d8n3q",
        shorter: "Dana Cleaning Services and Spark Cleaning, in the app: https://keilot.com/s/d8n3q",
        warmer: "Happy to help! We have two great cleaning suppliers in the community, Dana Cleaning Services and Spark Cleaning. You can book them directly in the community app: https://keilot.com/s/d8n3q",
      },
    },
  },
  {
    key: "bakery",
    match: /בורקס|מאפ|עוג|bak|cake|pastr|bourekas/i,
    status: "ready",
    question: { he: "איפה אפשר להזמין מאפים לשבת?", en: "Where can I order pastries for the weekend?" },
    verdict: {
      he: "מאפיית השכונה מוכרת בקהילה, והשבוע נוסף אצלה מגש בורקס ביתי.",
      en: "The Neighborhood Bakery sells in the community and added a homemade bourekas tray this week.",
    },
    steps: [
      { text: replyOnly },
      {
        text: { he: "כדאי גם לשתף את המוצר החדש בקבוצה.", en: "Consider sharing the new product in the group too." },
        link: "#share-grid",
        linkLabel: { he: "למה אפשר לשתף השבוע", en: "What to share this week" },
      },
    ],
    replies: {
      he: {
        default: "מאפיית השכונה מוכרת אצלנו בקהילה, והשבוע יש אצלה גם מגש בורקס ביתי. להזמנה: https://keilot.com/s/b3k7z",
        shorter: "מאפיית השכונה, דרך האפליקציה: https://keilot.com/s/b3k7z",
        warmer: "יש לנו בדיוק את זה! מאפיית השכונה מוכרת אצלנו בקהילה, והשבוע הוסיפו מגש בורקס ביתי שכדאי לנסות. להזמנה: https://keilot.com/s/b3k7z",
      },
      en: {
        default: "The Neighborhood Bakery sells in our community, and this week they also have a homemade bourekas tray. To order: https://keilot.com/s/b3k7z",
        shorter: "The Neighborhood Bakery, in the app: https://keilot.com/s/b3k7z",
        warmer: "We've got just the thing! The Neighborhood Bakery sells in our community, and this week they added a homemade bourekas tray worth trying. To order: https://keilot.com/s/b3k7z",
      },
    },
  },
  {
    key: "project",
    match: /פרויקט|מועדון|שיפוץ|project|renovat|club/i,
    status: "ready",
    question: { he: "מה קורה עם השיפוץ של המועדון?", en: "What's happening with the club renovation?" },
    verdict: { he: "פרויקט שיפוץ מועדון הדיירים הושלם.", en: "The residents' club renovation project is completed." },
    steps: [{ text: replyOnly }],
    replies: {
      he: {
        default: "השיפוץ של מועדון הדיירים הושלם! תודה לכל מי שלקח חלק. כל הפרטים כאן: https://keilot.com/s/c4m9x",
        shorter: "השיפוץ הושלם. פרטים: https://keilot.com/s/c4m9x",
        warmer: "איזה כיף לספר: השיפוץ של מועדון הדיירים הושלם! תודה ענקית לכל מי שלקח חלק. כל הפרטים כאן: https://keilot.com/s/c4m9x",
      },
      en: {
        default: "The residents' club renovation is completed! Thanks to everyone who took part. All the details: https://keilot.com/s/c4m9x",
        shorter: "The renovation is done. Details: https://keilot.com/s/c4m9x",
        warmer: "So happy to share: the residents' club renovation is completed! A huge thank you to everyone who took part. All the details: https://keilot.com/s/c4m9x",
      },
    },
  },
  {
    key: "discount",
    match: /הנח|הטב|מבצע|קופון|discount|deal|coupon/i,
    status: "ready",
    question: { he: "יש הנחות השבוע?", en: "Any discounts this week?" },
    verdict: { he: "יש השבוע 2 הטבות פעילות לחברי הקהילה.", en: "There are 2 active discounts for community members this week." },
    steps: [
      { text: replyOnly },
      {
        text: { he: "כדאי גם לשתף את ההטבה החדשה בקבוצה.", en: "Consider sharing the new discount in the group too." },
        link: "#share-grid",
        linkLabel: { he: "למה אפשר לשתף השבוע", en: "What to share this week" },
      },
    ],
    replies: {
      he: {
        default: "יש השבוע 15% הנחה על מגשי אירוח אצל טעמים קייטרינג, ו-1+1 על קפה בקפה פינה. כל ההטבות כאן: https://keilot.com/s/t5h1w",
        shorter: "15% על מגשי אירוח ו-1+1 על קפה: https://keilot.com/s/t5h1w",
        warmer: "יש, ושווה לנצל! השבוע יש 15% הנחה על מגשי אירוח אצל טעמים קייטרינג, ו-1+1 על קפה בקפה פינה. כל ההטבות מחכות לכם כאן: https://keilot.com/s/t5h1w",
      },
      en: {
        default: "This week there's 15% off party platters at Teamim Catering and buy one, get one coffee at Cafe Pina. All discounts: https://keilot.com/s/t5h1w",
        shorter: "15% off platters and BOGO coffee: https://keilot.com/s/t5h1w",
        warmer: "Yes, and they're worth it! This week there's 15% off party platters at Teamim Catering and buy one, get one coffee at Cafe Pina. All discounts are waiting here: https://keilot.com/s/t5h1w",
      },
    },
  },
  {
    key: "event",
    match: /אירוע|גינון|סדנ|event|workshop|garden/i,
    status: "ready",
    question: { he: "מתי ערב הגינון?", en: "When is the gardening evening?" },
    verdict: {
      he: "ערב הגינון הקהילתי מתקיים מחר ב-19:00 בגינת הבניין, ויש עוד מקום.",
      en: "The community gardening evening is tomorrow at 7:00 PM in the building garden, with spots left.",
    },
    steps: [{ text: replyOnly }],
    replies: {
      he: {
        default: "ערב הגינון הקהילתי מחר ב-19:00 בגינת הבניין. אפשר להירשם כאן: https://keilot.com/s/g7k2p",
        shorter: "מחר ב-19:00 בגינת הבניין: https://keilot.com/s/g7k2p",
        warmer: "מחר ב-19:00 בגינת הבניין, ויהיה כיף! עוד יש מקום, אפשר להירשם כאן: https://keilot.com/s/g7k2p",
      },
      en: {
        default: "The community gardening evening is tomorrow at 7:00 PM in the building garden. Sign up here: https://keilot.com/s/g7k2p",
        shorter: "Tomorrow, 7:00 PM, building garden: https://keilot.com/s/g7k2p",
        warmer: "Tomorrow at 7:00 PM in the building garden, and it's going to be fun! There are still spots, sign up here: https://keilot.com/s/g7k2p",
      },
    },
  },
  {
    key: "goal",
    match: /יעד|קופה|goal|piggy/i,
    status: "ready",
    question: { he: "כמה חסר ליעד של החודש?", en: "How close are we to this month's goal?" },
    verdict: { he: "הקהילה הגיעה ל-84% מהיעד החודשי.", en: "The community has reached 84% of its monthly goal." },
    steps: [
      { text: replyOnly },
      {
        text: { he: "כדאי גם לשתף את ההתקדמות בקבוצה.", en: "Consider sharing the progress in the group too." },
        link: "#share-grid",
        linkLabel: { he: "למה אפשר לשתף השבוע", en: "What to share this week" },
      },
    ],
    replies: {
      he: {
        default: "הגענו ל-84% מהיעד החודשי של הקהילה! כל הזמנה דרך האפליקציה מקרבת אותנו: https://keilot.com/s/f2r8d",
        shorter: "84% מהיעד החודשי: https://keilot.com/s/f2r8d",
        warmer: "אנחנו ממש קרובים! הגענו ל-84% מהיעד החודשי של הקהילה, וכל הזמנה דרך האפליקציה מקרבת אותנו: https://keilot.com/s/f2r8d",
      },
      en: {
        default: "We've reached 84% of the community's monthly goal! Every order through the app gets us closer: https://keilot.com/s/f2r8d",
        shorter: "84% of the monthly goal: https://keilot.com/s/f2r8d",
        warmer: "We're so close! We've reached 84% of the community's monthly goal, and every order through the app gets us closer: https://keilot.com/s/f2r8d",
      },
    },
  },
  {
    key: "recommendation",
    match: /אינסטלטור|אינסטלציה|plumb/i,
    status: "ready",
    question: { he: "מי אינסטלטור טוב?", en: "Who's a good plumber?" },
    verdict: {
      he: "אבי אינסטלציה בקהילה, עם הדירוג הגבוה ביותר החודש (4.9).",
      en: "Avi Plumbing is in the community, with this month's top rating (4.9).",
    },
    steps: [{ text: replyOnly }],
    replies: {
      he: {
        default: "ממליצים על אבי אינסטלציה, עם הדירוג הכי גבוה בקהילה החודש. להזמנה: https://keilot.com/s/a9p4l",
        shorter: "אבי אינסטלציה: https://keilot.com/s/a9p4l",
        warmer: "יש לנו בדיוק את מי שצריך! אבי אינסטלציה קיבלו את הדירוג הכי גבוה בקהילה החודש. להזמנה: https://keilot.com/s/a9p4l",
      },
      en: {
        default: "We recommend Avi Plumbing, the community's top rated supplier this month. To book: https://keilot.com/s/a9p4l",
        shorter: "Avi Plumbing: https://keilot.com/s/a9p4l",
        warmer: "We've got just the right one! Avi Plumbing got the community's top rating this month. To book: https://keilot.com/s/a9p4l",
      },
    },
  },
  {
    key: "join",
    match: /מצטרפ|להצטרף|הצטרפות|join/i,
    status: "ready",
    question: { he: "איך מצטרפים לקהילה?", en: "How do I join the community?" },
    verdict: {
      he: "זו שאלה על הצטרפות. אחרי ההרשמה, הבקשה תגיע אליכם לאישור.",
      en: "This is a question about joining. After signing up, the request comes to you for approval.",
    },
    steps: [{ text: { he: "לענות בהודעה הזו עם לינק ההצטרפות.", en: "Reply with this message and the join link." } }],
    replies: {
      he: {
        default: "אפשר להצטרף לקהילה דרך הלינק הזה: https://keilot.com/s/join-nsh. אחרי ההרשמה נאשר את הבקשה.",
        shorter: "להצטרפות: https://keilot.com/s/join-nsh",
        warmer: "איזה כיף שרוצים להצטרף! נרשמים דרך הלינק הזה: https://keilot.com/s/join-nsh, ואנחנו נאשר את הבקשה ממש מהר.",
      },
      en: {
        default: "You can join the community through this link: https://keilot.com/s/join-nsh. We'll approve the request after you sign up.",
        shorter: "To join: https://keilot.com/s/join-nsh",
        warmer: "So glad you want to join! Sign up through this link: https://keilot.com/s/join-nsh, and we'll approve the request quickly.",
      },
    },
  },
  {
    key: "second_hand",
    match: /יד שנייה|מוכר|למכור|עגלה|second-hand|for sale|selling/i,
    status: "ready",
    question: { he: "מישהו מוכר עגלת תינוק?", en: "Is anyone selling a stroller?" },
    verdict: { he: "פורסמו השבוע 18 פריטים ביד שנייה בקהילה.", en: "18 second-hand items were posted in the community this week." },
    steps: [{ text: { he: "לענות בהודעה הזו, עם הצעה לפרסם שם בקשה.", en: "Reply with this message, suggesting they post a request there." } }],
    replies: {
      he: {
        default: "שווה להציץ בלוח היד השנייה של הקהילה, פורסמו השבוע 18 פריטים: https://keilot.com/s/s18ya. אם אין, אפשר לפרסם שם שמחפשים.",
        shorter: "לוח היד השנייה של הקהילה: https://keilot.com/s/s18ya",
        warmer: "יכול להיות שמישהו בקהילה בדיוק מוכר! פורסמו השבוע 18 פריטים ביד שנייה: https://keilot.com/s/s18ya. ואם אין, אפשר לפרסם שם שמחפשים.",
      },
      en: {
        default: "Worth checking the community's second-hand board, 18 items were posted this week: https://keilot.com/s/s18ya. If it's not there, you can post a request.",
        shorter: "Community second-hand board: https://keilot.com/s/s18ya",
        warmer: "Someone in the community might be selling one! 18 second-hand items were posted this week: https://keilot.com/s/s18ya. And if not, you can post that you're looking.",
      },
    },
  },
  {
    key: "rides",
    match: /טרמפ|נוסע|נסיעה|ride|lift/i,
    status: "ready",
    question: { he: "מישהו נוסע מחר לתל אביב?", en: "Is anyone driving to Tel Aviv tomorrow?" },
    verdict: {
      he: "בלוח הטרמפים של הקהילה יש 2 נסיעות לתל אביב מחר.",
      en: "The community rides board has 2 rides to Tel Aviv tomorrow.",
    },
    steps: [{ text: replyOnly }],
    replies: {
      he: {
        default: "יש בלוח הטרמפים של הקהילה 2 נסיעות לתל אביב מחר. אפשר לשריין מקום כאן: https://keilot.com/s/r2d4t",
        shorter: "2 נסיעות לתל אביב מחר: https://keilot.com/s/r2d4t",
        warmer: "יש! בלוח הטרמפים של הקהילה מחכות 2 נסיעות לתל אביב מחר, אפשר לשריין מקום כאן: https://keilot.com/s/r2d4t",
      },
      en: {
        default: "The community rides board has 2 rides to Tel Aviv tomorrow. Book a seat here: https://keilot.com/s/r2d4t",
        shorter: "2 rides to Tel Aviv tomorrow: https://keilot.com/s/r2d4t",
        warmer: "Yes! The community rides board has 2 rides to Tel Aviv tomorrow, book a seat here: https://keilot.com/s/r2d4t",
      },
    },
  },
];

const FALLBACK = {
  key: "unclear",
  status: "action",
  question: null,
  verdict: {
    he: "לא מצאנו בהודעה שאלה שקשורה לקהילה.",
    en: "We couldn't find a community-related question in this message.",
  },
  steps: [
    {
      text: {
        he: "להדביק את ההודעה המקורית מהקבוצה, או צילום מסך שלה.",
        en: "Paste the original message from the group, or a screenshot of it.",
      },
    },
  ],
  replies: null,
};

const pick = (value, lang) => (value ? value[lang] ?? value.en : null);

// Where the question was asked.
export const ASKER_SOURCES = ["whatsapp_group", "facebook_group", "private", "other"];

/**
 * A message copied from WhatsApp starts with its sender —
 * "[15.9, 09:41] Name: text" or "Name: text". Returns { name, message } when
 * that shape is found. (The real service also reads names off screenshots.)
 */
export const detectAsker = (text) => {
  if (!text) return null;
  const withoutStamp = text.replace(/^\s*\[[^\]]*\]\s*/, "");
  const match = withoutStamp.match(/^([^:\n[\]]{2,30}):\s([\s\S]+)$/);
  if (!match || /https?$/i.test(match[1])) return null;
  return { name: match[1].trim(), message: match[2].trim() };
};

// The mock "reads" this sender off any attached screenshot.
const SCREENSHOT_SENDER = "רונית שמש";

/**
 * Builds the saved answer for one request. Pure — used by the call and the
 * demo list. Keeps who asked and where, so the manager never has to remember.
 */
export const buildAnswer = ({ text, hasImage, uiLang, askerName, source, id, createdAt }) => {
  const detected = detectAsker(text);
  const message = detected ? detected.message : text;
  const scenario =
    SCENARIOS.find((s) => message && s.match.test(message)) || (hasImage ? SCENARIOS[0] : FALLBACK);
  // The reply follows the message's language; a screenshot is read as Hebrew.
  const replyLang = message ? (HEBREW.test(message) ? "he" : "en") : "he";
  const typedName = (askerName || "").trim();
  const name = typedName || detected?.name || (hasImage && !message ? SCREENSHOT_SENDER : "");

  return {
    id: id || `ans-${Date.now()}`,
    createdAt: createdAt || new Date().toISOString(),
    asker: { name, source: source || "whatsapp_group", detected: !typedName && !!name },
    input: { text: message || "", fromImage: !!hasImage && !message },
    question: message ? null : pick(scenario.question, replyLang),
    status: scenario.status,
    verdict: pick(scenario.verdict, uiLang),
    steps: scenario.steps.map((step) => ({
      text: pick(step.text, uiLang),
      link: step.link || null,
      linkLabel: pick(step.linkLabel, uiLang),
    })),
    replies: scenario.replies ? scenario.replies[replyLang] : null,
    replyLang,
  };
};

/** Opens the reply with the asker's first name, when we know it. */
export const personalizeReply = (reply, name, lang) => {
  const first = (name || "").trim().split(/\s+/)[0];
  if (!reply || !first) return reply;
  const greeting = lang === "he" ? "היי" : "Hi";
  return reply.startsWith(`${greeting}!`)
    ? reply.replace(`${greeting}!`, `${greeting} ${first}!`)
    : `${greeting} ${first}! ${reply}`;
};

/** Mocked AI call — resolves to the saved answer after a short delay. */
export const mockAnswer = (request) =>
  new Promise((resolve) => setTimeout(() => resolve(buildAnswer(request)), 900));

// "All situations": one saved answer per kind of result, each with who asked.
export const DEMO_INPUTS = [
  { text: "[15.9, 09:41] מיכל אברהם: מישהו מכיר חשמלאי טוב בשכונה?", source: "whatsapp_group" },
  { hasImage: true, source: "whatsapp_group" },
  { text: "האינסטלטור שהזמנתי לא הגיע ולא עונה", askerName: "אורי בן דוד", source: "private" },
  { text: "יש המלצה על מישהו לניקיון?", askerName: "שירה פרץ", source: "facebook_group" },
  { text: "Does anyone know where I can order pastries for Friday?", askerName: "Sarah Miller", source: "whatsapp_group" },
  { text: "מה קורה עם השיפוץ של המועדון?", askerName: "עומר גבאי", source: "whatsapp_group" },
  { text: "יש הנחות השבוע לחברי הקהילה?", askerName: "תמר רוזן", source: "facebook_group" },
  { text: "מתי ערב הגינון?", askerName: "איתי כץ", source: "whatsapp_group" },
  { text: "כמה חסר ליעד של החודש?", askerName: "ליאת חדד", source: "whatsapp_group" },
  { text: "מי אינסטלטור טוב?", askerName: "בני אוחיון", source: "private" },
  { text: "איך מצטרפים לקהילה?", askerName: "הדס נחום", source: "facebook_group" },
  { text: "מישהו מוכר עגלת תינוק?", askerName: "רחל גולן", source: "whatsapp_group" },
  { text: "מישהו נוסע מחר לתל אביב?", askerName: "עידו שחר", source: "whatsapp_group" },
  { text: "מזל טוב לשכנים מקומה 3!", source: "other" },
];

export const buildDemoAnswers = (uiLang) => {
  const now = Date.now();
  return DEMO_INPUTS.map((input, i) =>
    buildAnswer({
      ...input,
      uiLang,
      id: `demo-${i}`,
      createdAt: new Date(now - (i + 1) * 47 * 60 * 1000).toISOString(),
    }),
  );
};
